import {
  Button,
  CheckboxListItem,
  Divider,
  LinkText,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { useUserActivation } from '~/api/query/userActivationData';
import { useUserConsentsMutation } from '~/api/query/userConsents';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';
import { publicRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/activation/$token/legal')({
  component: RouteComponent,
  beforeLoad: publicRoute,
});

const ACTIVATION_FLOW_ID = 'activation-flow-legal';

const schema = z.object({
  termsOfUse: z.boolean().refine((val) => val, { message: 'missing to check' }),
  privacyNotice: z
    .boolean()
    .refine((val) => val, { message: 'missing to check' }),
});

function RouteComponent() {
  const { openModal, closeModal } = useModal();
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const userConsents = useUserConsentsMutation();
  // Get Token Data to verify still valid
  const { data: tokenData, isLoading } = useUserActivation({
    token,
  });

  const form = useForm({
    defaultValues: {
      termsOfUse: false,
      privacyNotice: false,
    },
    validators: {
      onChange: schema,
      onMount: schema,
    },
    onSubmit: async ({ value }) => {
      const { termsOfUse, privacyNotice } = value;

      const response = await userConsents.mutateAsync({
        termsOfUse: termsOfUse ? 'accept' : 'decline',
        privacyNotice: privacyNotice ? 'accept' : 'decline',
      });

      if (!response.termsOfUse.accepted || !response.privacyNotice.accepted) {
        throw new Error('Not all consents accepted');
      }

      closeModal('TermsOfUse');
      closeModal('PrivacyNotice');
      void navigate({ to: `/activation/${token}/account`, replace: true });
    },
  });

  const handleTermsOfUse = () => {
    openModal('TermsOfUse', {
      testID: ACTIVATION_FLOW_ID,
      onClose: () => {
        closeModal('TermsOfUse');
      },
    });
  };
  const handlePrivacyNotice = () => {
    openModal('PrivacyNotice', {
      testID: ACTIVATION_FLOW_ID,
      onClose: () => {
        closeModal('PrivacyNotice');
      },
    });
  };

  return !tokenData?.id && !isLoading ? (
    <Stack
      borderWidth="$0"
      maxWidth={800}
      width="100%"
      borderRadius="$1.5"
      backgroundColor="$surface.container"
      padding="$6"
      marginHorizontal="auto"
      marginTop="$9"
      gap="$6"
    >
      <Text type="$h3.strong">Activation link expired</Text>
      <Text type="$bodyBase.default">
        For security reasons, activation links expire after a short time. Please
        reach out to your administrator to request a new one.{' '}
      </Text>
      <Text type="$bodyBase.default">
        If your account has already been activated, you can try logging in.
      </Text>
      <Stack alignContent="flex-end" alignItems="flex-end">
        <Stack border="$1" width={164}>
          <Button
            variant="primary"
            type="button"
            size="large"
            label="Log in"
            onPress={() => {
              void navigate({ to: '/login' });
            }}
            testID={buildTestId({
              testID: ACTIVATION_FLOW_ID,
              suffix: ['signin', 'button'],
            })}
          />
        </Stack>
      </Stack>
    </Stack>
  ) : (
    <Stack
      borderWidth="$0"
      maxWidth={800}
      width="100%"
      borderRadius="$1.5"
      backgroundColor="$surface.container"
      padding="$6"
      marginHorizontal="auto"
      marginTop="$9"
    >
      <Stack gap="$6" marginBottom="$6">
        <Text type="$h3.strong">Legal notices</Text>
        <Text type="$bodyBase.medium">
          Welcome to LactateView. Please carefully read the{' '}
          <LinkText
            type="$bodyBase.default"
            onPress={handlePrivacyNotice}
            testID={buildTestId({
              testID: ACTIVATION_FLOW_ID,
              suffix: ['view', 'privacy', 'notice', 'link'],
            })}
          >
            Privacy Notice
          </LinkText>{' '}
          and{' '}
          <LinkText
            type="$bodyBase.default"
            onPress={handleTermsOfUse}
            testID={buildTestId({
              testID: ACTIVATION_FLOW_ID,
              suffix: ['view', 'terms', 'of', 'use', 'link'],
            })}
          >
            Terms of Use
          </LinkText>{' '}
          to understand your rights and how we handle and use your personal
          information. The Privacy Notice explains how the company, collects,
          secures, stores, and shares your personal information.
        </Text>
      </Stack>

      <Stack flexDirection="row" marginBottom="$6" gap="$4">
        <Stack
          borderRadius="$1.5"
          borderWidth={1}
          paddingTop="$2"
          borderColor="$neutral.20"
        >
          <form.Field name="termsOfUse">
            {(field) => (
              <CheckboxListItem
                title="Terms of Use"
                label="I have read and accept the Terms of Use."
                checked={field.state.value}
                onPress={field.handleChange}
                disabled={false}
                testID={buildTestId({
                  testID: ACTIVATION_FLOW_ID,
                  suffix: ['checkbox', 'terms', 'of', 'use'],
                })}
              />
            )}
          </form.Field>

          <Divider marginHorizontal="$4" marginTop="$2" />
          <Button
            variant="tertiary"
            type="button"
            size="large"
            label="Read full Terms of Use"
            onPress={handleTermsOfUse}
            testID={buildTestId({
              testID: ACTIVATION_FLOW_ID,
              suffix: ['view', 'terms', 'of', 'use', 'button'],
            })}
          />
        </Stack>
        <Stack
          borderRadius="$1.5"
          borderWidth={1}
          paddingTop="$2"
          borderColor="$neutral.20"
        >
          <form.Field name="privacyNotice">
            {(field) => (
              <CheckboxListItem
                title="Privacy Notice"
                label="I have read and understand the Privacy Notice. "
                checked={field.state.value}
                onPress={field.handleChange}
                disabled={false}
                testID={buildTestId({
                  testID: ACTIVATION_FLOW_ID,
                  suffix: ['checkbox', 'privacy', 'notice'],
                })}
              />
            )}
          </form.Field>
          <Divider marginHorizontal="$4" marginTop="$2" />
          <Button
            variant="tertiary"
            type="button"
            size="large"
            label="Read full Privacy Notice"
            onPress={handlePrivacyNotice}
            testID={buildTestId({
              testID: ACTIVATION_FLOW_ID,
              suffix: ['view', 'privacy', 'notice', 'button'],
            })}
          />
        </Stack>
      </Stack>

      <Stack alignContent="flex-end" alignItems="flex-end">
        <Stack border="$1" width={164}>
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isPristine,
            ]}
          >
            {([canSubmit, isSubmitting, isPristine]) => {
              const disabled = !canSubmit || isSubmitting;

              return (
                <Button
                  variant="primary"
                  type="button"
                  size="large"
                  label={isSubmitting ? 'Sending...' : 'Next'}
                  disabled={disabled ?? isPristine}
                  onPress={() => {
                    void form.handleSubmit();
                  }}
                  testID={buildTestId({
                    testID: ACTIVATION_FLOW_ID,
                    suffix: ['next', 'button', 'submit'],
                  })}
                />
              );
            }}
          </form.Subscribe>
        </Stack>
      </Stack>
    </Stack>
  );
}
